import { AlertTriangle, Check, Loader2, Search, Trash2, Users, X } from "lucide-react";
import { useMemo, useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import {
  useM3ClienteEmail,
  useM3EliminaClienteEmail,
  useM3RisolviPropostaEmail,
  useM3UpsertClienteEmail,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { ClienteEmail, ClienteEmailProposta } from "@/types/api";

const INPUT =
  "h-9 w-full rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const FONTE_LABEL: Record<ClienteEmail["fonte"], { label: string; cls: string }> = {
  anagrafica: { label: "anagrafica", cls: "bg-sky-100 text-sky-800" },
  confermata: { label: "confermata", cls: "bg-emerald-100 text-emerald-800" },
  appresa: { label: "appresa", cls: "bg-muted text-muted-foreground" },
};

export function AssociazioniEmailSection() {
  const { data, isLoading } = useM3ClienteEmail();
  const [q, setQ] = useState("");

  const visibili = useMemo(() => {
    const items = data?.items ?? [];
    const t = q.toLowerCase().trim();
    if (!t) return items;
    return items.filter(
      (r) =>
        r.email.includes(t) ||
        String(r.cliente_codice).includes(t) ||
        (r.cliente_nome ?? "").toLowerCase().includes(t),
    );
  }, [data, q]);

  if (isLoading) return <Skeleton className="h-64 w-full max-w-3xl" />;
  if (!data) return null;

  const puoModificare = data.puo_modificare;

  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-foreground">
          Associazioni email → cliente
        </h2>
        <p className="text-xs text-muted-foreground">
          Mappa che Aida usa per riconoscere il cliente dalle email in arrivo.
          Alimentata dall'anagrafica, dagli apprendimenti di Aida e dalle conferme
          manuali. {!puoModificare && "(sola lettura per il tuo profilo)"}
        </p>
      </div>

      {/* Coda conferme */}
      {data.proposte.length > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
            <AlertTriangle className="size-4" />
            Da confermare ({data.da_confermare})
          </div>
          <div className="space-y-2">
            {data.proposte.map((p) => (
              <PropostaRow key={p.id} p={p} puoModificare={puoModificare} />
            ))}
          </div>
        </div>
      )}

      {/* Aggiunta manuale */}
      {puoModificare && <AggiungiAssociazione />}

      {/* Tabella associazioni */}
      <div className="space-y-2">
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca email, cliente o codice…"
            className={cn(INPUT, "pl-8")}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {data.totali} associazioni totali
        </p>
        {visibili.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nessuna associazione.</p>
        ) : (
          <div className="max-w-3xl overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Cliente</th>
                  <th className="px-3 py-2 text-left">Fonte</th>
                  {puoModificare && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody>
                {visibili.map((r) => (
                  <EmailRow key={r.email} r={r} puoModificare={puoModificare} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function PropostaRow({
  p,
  puoModificare,
}: {
  p: ClienteEmailProposta;
  puoModificare: boolean;
}) {
  const risolvi = useM3RisolviPropostaEmail();
  const condivisa = p.motivo === "condivisa_multi_cliente";
  const [scelto, setScelto] = useState<number>(p.cliente_codice_proposto);

  function nome(cod: number | null): string {
    if (cod == null) return "—";
    if (cod === p.cliente_codice_proposto && p.cliente_proposto_nome)
      return p.cliente_proposto_nome;
    if (cod === p.cliente_codice_attuale && p.cliente_attuale_nome)
      return p.cliente_attuale_nome;
    return p.candidati_nomi?.[cod] ?? `Cliente ${cod}`;
  }

  return (
    <div className="rounded-md border bg-card p-2.5 text-sm">
      <div className="flex items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
          {condivisa ? <Users className="size-3" /> : <AlertTriangle className="size-3" />}
          {condivisa ? "condivisa" : "conflitto"}
        </span>
        <span className="font-mono text-xs">{p.email}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {condivisa ? (
          <>Indirizzo usato da più clienti: scegli a chi associarlo.</>
        ) : (
          <>
            L'anagrafica propone{" "}
            <strong className="text-foreground">{nome(p.cliente_codice_proposto)}</strong>
            {p.cliente_codice_attuale != null && (
              <>
                , ma oggi è associata a{" "}
                <strong className="text-foreground">{nome(p.cliente_codice_attuale)}</strong>
              </>
            )}
            .
          </>
        )}
      </p>

      {puoModificare && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {condivisa ? (
            <select
              className="h-8 rounded border bg-card px-1.5 text-xs"
              value={scelto}
              onChange={(e) => setScelto(Number(e.target.value))}
            >
              {p.candidati.map((c) => (
                <option key={c} value={c}>
                  {nome(c)} #{c}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-xs text-muted-foreground">
              Conferma → <strong>{nome(p.cliente_codice_proposto)}</strong>
            </span>
          )}
          <button
            type="button"
            disabled={risolvi.isPending}
            onClick={() =>
              risolvi.mutate({
                id: p.id,
                body: {
                  azione: "conferma",
                  cliente_codice: condivisa ? scelto : p.cliente_codice_proposto,
                },
              })
            }
            className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Check className="size-3.5" />
            Conferma
          </button>
          <button
            type="button"
            disabled={risolvi.isPending}
            onClick={() => risolvi.mutate({ id: p.id, body: { azione: "rifiuta" } })}
            className="inline-flex h-8 items-center gap-1 rounded-md border px-2.5 text-xs hover:bg-accent disabled:opacity-50"
          >
            <X className="size-3.5" />
            Rifiuta
          </button>
        </div>
      )}
    </div>
  );
}

function EmailRow({
  r,
  puoModificare,
}: {
  r: ClienteEmail;
  puoModificare: boolean;
}) {
  const upsert = useM3UpsertClienteEmail();
  const elimina = useM3EliminaClienteEmail();
  const [editing, setEditing] = useState(false);
  const [cod, setCod] = useState(String(r.cliente_codice));
  const fonte = FONTE_LABEL[r.fonte] ?? FONTE_LABEL.appresa;

  return (
    <tr className="border-t align-middle">
      <td className="px-3 py-1.5 font-mono text-xs">{r.email}</td>
      <td className="px-3 py-1.5">
        {editing ? (
          <input
            className="h-7 w-28 rounded border bg-card px-1.5 text-xs"
            value={cod}
            onChange={(e) => setCod(e.target.value)}
          />
        ) : (
          <>
            {r.cliente_nome || `Cliente ${r.cliente_codice}`}{" "}
            <span className="font-mono text-xs text-muted-foreground">
              #{r.cliente_codice}
            </span>
          </>
        )}
      </td>
      <td className="px-3 py-1.5">
        <span className={cn("rounded px-1.5 py-0.5 text-[11px]", fonte.cls)}>
          {fonte.label}
        </span>
      </td>
      {puoModificare && (
        <td className="px-3 py-1.5 text-right">
          {editing ? (
            <div className="inline-flex gap-1">
              <button
                type="button"
                disabled={upsert.isPending || !Number(cod)}
                onClick={() =>
                  upsert.mutate(
                    { email: r.email, body: { cliente_codice: Number(cod) } },
                    { onSuccess: () => setEditing(false) },
                  )
                }
                className="rounded p-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                title="Salva"
              >
                {upsert.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Check className="size-4" />
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCod(String(r.cliente_codice));
                  setEditing(false);
                }}
                className="rounded p-1 text-muted-foreground hover:bg-muted"
                title="Annulla"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : (
            <div className="inline-flex gap-1">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Ri-punta
              </button>
              <button
                type="button"
                onClick={() => elimina.mutate(r.email)}
                className="rounded p-1 text-muted-foreground hover:text-red-600"
                title="Elimina associazione"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

function AggiungiAssociazione() {
  const upsert = useM3UpsertClienteEmail();
  const [email, setEmail] = useState("");
  const [cod, setCod] = useState("");

  function aggiungi() {
    const addr = email.trim().toLowerCase();
    const c = Number(cod);
    if (!addr || !addr.includes("@") || !c) return;
    upsert.mutate(
      { email: addr, body: { cliente_codice: c } },
      {
        onSuccess: () => {
          setEmail("");
          setCod("");
        },
      },
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="space-y-1 text-xs text-muted-foreground">
        <span>Email</span>
        <input
          className={INPUT}
          style={{ width: 240 }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="cliente@dominio.it"
        />
      </label>
      <label className="space-y-1 text-xs text-muted-foreground">
        <span>Codice cliente</span>
        <input
          className={INPUT}
          style={{ width: 140 }}
          value={cod}
          onChange={(e) => setCod(e.target.value)}
          placeholder="es. 8020992"
        />
      </label>
      <button
        type="button"
        onClick={aggiungi}
        disabled={upsert.isPending || !email || !cod}
        className="h-9 rounded-md border px-3 text-sm hover:bg-accent disabled:opacity-50"
      >
        Aggiungi associazione
      </button>
    </div>
  );
}
